import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useOrganisationApi } from "@/lib/api/organisation/useOrganisationApi";
import { useOrganisationContext } from "@/lib/context/OrganisationProvider";
import { useToast } from "@/lib/hooks";
import { useLogoutModal } from "@/lib/hooks/useLogoutModal";
import { useUserData } from "@/lib/hooks/useUserData";

import styles from "./OrganisationOnboardingModal.module.scss";

export const OrganisationOnboardingModal = (): JSX.Element | null => {
  const { t } = useTranslation("translation");
  const { publish } = useToast();
  const { createOrganisation } = useOrganisationApi();
  const { needsOnboarding, refetchOrganisation, setOrganisation } =
    useOrganisationContext();

  const [organisationName, setOrganisationName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { userIdentityData } = useUserData();

  const { handleLogout } = useLogoutModal();

  useEffect(() => {
    if (userIdentityData && !userIdentityData.active) {
      publish({
        variant: "danger",
        text: t("userNotActiveContactAdmin"),
      });
      void handleLogout();
    }
  }, [userIdentityData, publish, handleLogout]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!organisationName.trim()) {
      publish({
        variant: "danger",
        text: t("pleaseEnterOrganisationName"),
      });

      return;
    }

    try {
      setIsSubmitting(true);
      const newOrg = await createOrganisation({
        name: organisationName.trim(),
      });
      setOrganisation(newOrg);
      await refetchOrganisation();
      publish({
        variant: "success",
        text: t("organisationCreatedSuccessfully"),
      });
    } catch (error) {
      console.error("Error creating organisation:", error);
      publish({
        variant: "danger",
        text: t("failedToCreateOrganisation"),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!needsOnboarding) {
    return null;
  }

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2 className={styles.title}>{t("welcomeToGraipAgentLabs")}</h2>
          <p className={styles.subtitle}>
            {t("setupOrganisationToGetStarted")}
          </p>
        </div>

        <form
          onSubmit={(e) => {
            void handleSubmit(e);
          }}
          className={styles.form}
        >
          <div className={styles.field}>
            <label htmlFor="organisationName" className={styles.label}>
              {t("organisationName")}
            </label>
            <input
              id="organisationName"
              type="text"
              value={organisationName}
              onChange={(e) => setOrganisationName(e.target.value)}
              placeholder={t("enterOrganisationName")}
              className={styles.input}
              disabled={isSubmitting}
              autoFocus
            />
          </div>

          <button
            type="submit"
            className={styles.submitButton}
            disabled={isSubmitting || !organisationName.trim()}
          >
            {isSubmitting ? t("creating") : t("createOrganisation")}
          </button>
        </form>

        <p className={styles.note}>{t("organisationAdminNote")}</p>
      </div>
    </div>
  );
};
